/**
 * useDictation — push-to-talk speech-to-text.
 *
 * Primary: AWS Transcribe Streaming (browser → Cognito Identity Pool →
 * Transcribe WebSocket). Works in Firefox, Chrome, Safari, mobile —
 * anywhere getUserMedia is available. This is the same architecture
 * SecurityBot-dev uses.
 *
 * Fallback: browser Web Speech API. Used automatically if
 *   • Cognito init fails (e.g. local dev with no backend)
 *   • the Cognito pool isn't reachable
 *   • the SAM stack hasn't been updated yet
 *
 * Caller pushes the mic button → start() initialises (lazy) and opens the
 * mic. While listening, onInterim is called as the user speaks (so the
 * chat composer can show a live transcript). When the user stops or hits
 * the mic again, stop() returns the final transcript.
 *
 * No silence detection in this MVP — the user explicitly clicks stop
 * (or the existing send action). Auto-send-on-silence is a Phase-3 polish.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { getTranscribe } from '../services/voice/TranscribeService';

interface DictationCallbacks {
  /** Fired on every partial recognition result while listening. */
  onInterim?: (transcript: string) => void;
  /** Fired when listening ends (with the final concatenated transcript). */
  onFinal?:   (transcript: string) => void;
  /** Fired on errors (no-mic-permission, network). */
  onError?:   (error: string) => void;
}

export function useDictation(callbacks?: DictationCallbacks) {
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [supported, setSupported] = useState<boolean>(true); // optimistic; verified on first start

  const cbRef = useRef<DictationCallbacks | undefined>(callbacks);
  cbRef.current = callbacks;

  // Pre-warm the Transcribe client (fetch Cognito creds) on mount so the
  // first user click starts streaming within ~100 ms instead of ~1.5s.
  // Errors are swallowed — startCapture will surface them and the
  // Web Speech fallback kicks in inside the service.
  useEffect(() => {
    const svc = getTranscribe();
    setSupported(svc.isSupported);
    svc.initialize().catch(() => { /* fallback handles it */ });
    return () => {
      // Don't dispose — service is a module-level singleton shared
      // across the app. We just stop any ongoing capture.
      if (svc.isRecording) svc.stopCapture();
    };
  }, []);

  const start = useCallback(async () => {
    const svc = getTranscribe();
    if (!svc.isSupported) {
      cbRef.current?.onError?.('Voice input not available — your browser does not allow microphone access.');
      return;
    }
    if (svc.isRecording) return;

    try {
      // Make sure init has run (no-op if already done)
      await svc.initialize();
    } catch (err: any) {
      cbRef.current?.onError?.(err?.message || 'Failed to initialise voice');
      return;
    }

    setTranscript('');
    setListening(true);

    await svc.startCapture(
      (live) => {
        setTranscript(live);
        cbRef.current?.onInterim?.(live);
      },
      (msg) => {
        cbRef.current?.onError?.(msg);
        setListening(false);
      },
    );
  }, []);

  const stop = useCallback(() => {
    const svc = getTranscribe();
    if (!svc.isRecording) {
      setListening(false);
      return;
    }
    const finalText = svc.stopCapture();
    setListening(false);
    if (finalText) cbRef.current?.onFinal?.(finalText);
  }, []);

  const toggle = useCallback(() => {
    if (listening) stop(); else start();
  }, [listening, start, stop]);

  return { listening, supported, transcript, start, stop, toggle };
}
